import {
  hierarchy,
  HierarchyNode,
  HierarchyPointNode,
  tree,
} from 'd3-hierarchy';
import {
  linkRadial,
} from 'd3-shape';
import uniq from 'lodash-es/uniq';
import {
  keyByMap,
} from '../../Utils';
import flattenTree from './flattenTree';
import {
  centerNodePctOfChartRadius,
  firstLevelNodeDistanceFromCenterPctOfChartRadius,
  firstLevelNodeMaxPctOfChartRadius,
  getChordLength,
  ILink,
  ILinkEnd,
  INodesLinksLayoutOutput,
  polarToCartesian,
  secondLevelNodeMaxPctOfChartRadius,
} from './Utils';

const PI = Math.PI;

export const getSmallestDifference = (input: number[]): number => {
  const inputLength = input.length;
  if (inputLength < 2) {
    throw new Error('input must have length >= 2');
  } else if (inputLength === 2) {
    return Math.abs(input[1] - input[0]);
  } else {
    const sorted = input.sort((a, b) => a - b);
    const differences: number[] = [];
    for (let i = 1; i < inputLength; i = i + 1) {
      const difference = sorted[i] - sorted[i - 1];
      differences.push(difference);
    }
    return Math.min(...differences);
  }
};

// Determine the radius that can fit both radially and circumferentially:
export const getNodeRadius = (
    distanceOfNodesFromCenter: number,
    maxRadiusToFitRadially: number,
    nodesAtThisLevel: INodeCartesianAndPolar[],
  ) => {

  let nodeRadius: number, smallestAngularSeparation: number;
  if (nodesAtThisLevel.length === 1) {
    // Special handling when there's only one node a level: so that the text for
    // that node stays on one side of that node:
    nodeRadius = maxRadiusToFitRadially;
    smallestAngularSeparation = PI;
  } else {
    const thetasAtThisLevel = nodesAtThisLevel.map(({theta}) => theta);

    // Need to insert this extra element to account for the circular nature of
    // the ring. Otherwise, the distance between the first and last node won't
    // be taken into account:
    const augmentedThetasAtThisLevel = [
      ...thetasAtThisLevel,
      Math.min(...thetasAtThisLevel) + 2 * PI,
    ];
    const extraMarginAroundEachNode = 0.1;

    smallestAngularSeparation = getSmallestDifference(augmentedThetasAtThisLevel);
    // The smallest diameter to fit circumferentially is the distance between
    // the centers of the two closest nodes at the same level:
    const maxDiameterToFitCircumferentially = getChordLength(
      distanceOfNodesFromCenter, smallestAngularSeparation,
    );
    const maxRadiusToFitCircumferentially = 1 / 2 * maxDiameterToFitCircumferentially * (1 - extraMarginAroundEachNode);
    nodeRadius = Math.min(maxRadiusToFitCircumferentially, maxRadiusToFitRadially);
  }

  return {
    nodeRadius,
    smallestAngularSeparation,
  };
};

interface ILayoutPrecursor {
  id: number;
  children?: ILayoutPrecursor[];
}

interface INodePolar {
  id: number;
  r: number;
  theta: number;
  depth: number;
}

interface INodeCartesianAndPolar {
  id: number;
  x: number;
  y: number;
  // These are cached to generate radial links between nodes:
  r: number;
  theta: number;
  depth: number;
}

interface INodeWithSize {
  id: number;
  x: number;
  y: number;
  // These are cached to generate radial links between nodes:
  r: number;
  theta: number;
  depth: number;
  radius: number;
}

interface ILinkWithoutPath {
  source: ILinkEnd;
  target: ILinkEnd;
}

const separation = (a: HierarchyNode<ILayoutPrecursor>, b: HierarchyNode<ILayoutPrecursor>) =>
                    (((a.parent === b.parent) ? 1 : 2) / a.depth);

// "Canonical" link runs between nodes identified as direct parent and children
// in the input to the layout algorithm. It's possible for a parent to have
// multiple children but we must pick one child for the layout algorithm to
// work. The other children we be connected via "non-canonical" linkls:

interface INodeForLinkCreator {
  id: number;
  r: number;
  theta: number;
  radiusOfNode: number;
}
const createNonCanonicalLink =
  <T extends INodeForLinkCreator>(a: T, b: T): ILink => {

  let source: T, target: T;
  if (a.id < b.id) {
    source = a;
    target = b;
  } else {
    source = b;
    target = a;
  }
  const linkWithoutPath: ILinkWithoutPath = {
    source: {
      id: source.id,
      r: source.r,
      theta: source.theta + PI / 2,
    },
    target: {
      id: target.id,
      r: target.r,
      theta: target.theta + PI / 2,
    },
  };
  // For non-canonical links, draw a bezier curve from `source` to `target` whose
  // tangets are each point is radial:
  const sourceInputToPathCreator: ILinkEnd = {
    id: source.id,
    r: (source.r < target.r) ? source.r + source.radiusOfNode : source.r - source.radiusOfNode,
    theta: source.theta + PI / 2,
  };

  const targetInputToPathCreator: ILinkEnd = {
    id: target.id,
    r: (source.r < target.r) ? target.r - target.radiusOfNode : target.r + target.radiusOfNode,
    theta: target.theta + PI / 2,
  };
  const inputToPathCreator: ILinkWithoutPath = {
    source: sourceInputToPathCreator,
    target: targetInputToPathCreator,
  };
  const path = generateBezierLink(inputToPathCreator)!;
  const result: ILink = {
    ...linkWithoutPath,
    path,
  };
  return result;
};

const generateBezierLink = linkRadial<ILinkWithoutPath, ILinkEnd>().angle(({theta}) => theta).radius(({r}) => r);

const getLayout = (
    centerNodeId: number,
    chartRadius: number,
    edgeLookupTable: Record<string, number[]>,
  ): INodesLinksLayoutOutput => {

  // Need to filter out the center node becaues it surely will be in this array:
  const firstLevelIds = edgeLookupTable[centerNodeId].filter(id => id !== centerNodeId);
  const firstLevelIdSet = new Set(firstLevelIds);

  const undedupedSecondLevelConnections: number[] = [];
  for (const nodeId of firstLevelIds) {
    undedupedSecondLevelConnections.push(...edgeLookupTable[nodeId]);
  }
  // Need to filter out the center node and first-level nodes because they can
  // easily be in this array:
  const secondLevelIds = uniq(undedupedSecondLevelConnections).filter(
    id => id !== centerNodeId && !firstLevelIdSet.has(id),
  );
  const secondLevelIdSet = new Set(secondLevelIds);

  const parentChildRelationships: ILayoutPrecursor = {
    id: centerNodeId,
    children: [],
  };

  const firstLevelChildrenList = parentChildRelationships.children!;

  for (const node of firstLevelIds) {
    firstLevelChildrenList.push({
      id: node,
      children: [],
    });
  }

  const firstLevelConnectionsSet = new Set(firstLevelIds);
  for (const nodeId of secondLevelIds) {
    const connected = edgeLookupTable[nodeId];
    // Find out which first-level node this second-level node is connected to
    // (pick only one):
    const firstLevelNodeId = connected.find(id => firstLevelConnectionsSet.has(id))!;
    const firstLevelNodeDatum = firstLevelChildrenList.find(({id}) => id === firstLevelNodeId)!;
    firstLevelNodeDatum.children!.push({
      id: nodeId,
    });
  }

  const structure: HierarchyNode<ILayoutPrecursor> = hierarchy<ILayoutPrecursor>(parentChildRelationships);

  const performLayout = tree<ILayoutPrecursor>()
                  .size([2 * PI, chartRadius])
                  .separation(separation);
  const layout = performLayout(structure);
  const flatLayout: Array<HierarchyPointNode<ILayoutPrecursor>> = flattenTree(layout);

  const centerNodeRadius = chartRadius * centerNodePctOfChartRadius;

  const firstLevelNodeDistanceFromCenter = chartRadius * firstLevelNodeDistanceFromCenterPctOfChartRadius;
  const secondLevelNodeDistanceFromCenter = chartRadius;

  const flatLayoutPolarCoords: INodePolar[] = flatLayout.map(({x, y, data: {id}, depth}) => {
    // Need special treatment for first-level node because the layout
    // algorithm assumes that first-level nodes are midway between the center
    // node and second-level nodes but in reality we move the first-level node
    // closer to the center to make space for first-level node labels:
    const radius = (depth === 1) ? firstLevelNodeDistanceFromCenter : y;
    return {
      r: radius,
      theta: x,
      id, depth,
    };
  });

  const nodesWithoutSize: INodeCartesianAndPolar[] = flatLayoutPolarCoords.map(
    ({r, theta, id, depth}) => {
      const {x, y} = polarToCartesian({r, theta});
      return {
        id, r, theta, depth, x, y,
      };
    },
  );

  const {
    nodeRadius: firstLevelNodeRadius,
    smallestAngularSeparation: firstLevelSmallestAngularSeparation,
  } = getNodeRadius(
    firstLevelNodeDistanceFromCenter,
    chartRadius * firstLevelNodeMaxPctOfChartRadius,
    nodesWithoutSize.filter(({id}) => firstLevelIdSet.has(id)),
  );

  const {
    nodeRadius: secondLevelNodeRadius,
    smallestAngularSeparation: secondLevelSmallestAngularSeparation,
   } = getNodeRadius(
    secondLevelNodeDistanceFromCenter,
    chartRadius * secondLevelNodeMaxPctOfChartRadius,
    nodesWithoutSize.filter(({id}) => secondLevelIdSet.has(id)),
  );

  const unsortedNodesWithSize: INodeWithSize[] = nodesWithoutSize.map(node => {
    const {depth} = node;
    let radius: number;
    if (depth === 0) {
      radius = centerNodeRadius;
    } else if (depth === 1) {
      radius = firstLevelNodeRadius;
    } else {
      radius = secondLevelNodeRadius;
    }
    return {
      ...node,
      radius,
    };
  });

  // Sort to avoid unnecessary DOM mounting/unmounting:
  const nodesWithSize = unsortedNodesWithSize.sort((a, b) => a.id - b.id);

  const centerNode = nodesWithSize.find(({id}) => id === centerNodeId)!;

  const nodesWithSizeMap = keyByMap<number, INodeWithSize>(({id}) => id)(nodesWithSize);

  const allNodesSet: Set<number> = new Set([centerNodeId, ...firstLevelIds, ...secondLevelIds]);

  const links: ILink[] = [];
  // This is to keep track of which links have already been added as "direct" links so
  // that we only add "indirect" links as bezier curves:
  const directLinkHashes: string[] = [];

  const getLinkHash = (firstNodeId: number, secondNodeId: number) => {
    if (firstNodeId < secondNodeId) {
      return `${firstNodeId}-${secondNodeId}`;
    } else {
      return `${secondNodeId}-${firstNodeId}`;
    }
  };

  // Add links between nodes that run between direct parents and children in the
  // above hierarchy There are other links that run between non-parents and
  // children because one node can have multiple connections to other nodes:

  // Direct links between center node and first-level nodes:
  for (const {id: firstLevelId} of parentChildRelationships.children!) {
    const firstLevelNode = nodesWithSizeMap.get(firstLevelId)!;
    const link = createNonCanonicalLink(
      // Assign `theta` of `firstLevelNode` to center node so that the lines
      // eminating from it will be directly radially outward.
      {...centerNode, theta: firstLevelNode.theta, radiusOfNode: centerNodeRadius},
      {...firstLevelNode, radiusOfNode: firstLevelNodeRadius},
    );
    links.push(link);
    directLinkHashes.push(getLinkHash(centerNodeId, firstLevelId));
  }

  // Direct links between first-level and second-level nodes:
  for (const {id: firstLevelId, children} of parentChildRelationships.children!) {
    for (const {id: secondLevelId} of children!) {
      const firstLevelNode = nodesWithSizeMap.get(firstLevelId)!;
      const secondLevelNode = nodesWithSizeMap.get(secondLevelId)!;
      const link = createNonCanonicalLink(
        {...firstLevelNode, radiusOfNode: firstLevelNodeRadius},
        {...secondLevelNode, radiusOfNode: secondLevelNodeRadius},
      );
      links.push(link);
      directLinkHashes.push(getLinkHash(firstLevelId, secondLevelId));
    }
  }

  const drirectLinkHashesSet = new Set(directLinkHashes);

  // Indirect links:
  for (const {id: sourceId} of nodesWithSize) {
    const connectionsOfThisNode = edgeLookupTable[sourceId];
    for (const targetId of connectionsOfThisNode) {
      // Make sure the connection is added only once (by checking `sourceId` <
      // `targetId`) and that this connection is between two nodes present in
      // the rings graph (by checking the `target` is among the `allNodesSet`)
      // and that this connection is not already a direct connection:
      if (sourceId < targetId &&
          allNodesSet.has(targetId) &&
          !drirectLinkHashesSet.has(getLinkHash(sourceId, targetId))) {

        const source = nodesWithSizeMap.get(sourceId)!;
        const target = nodesWithSizeMap.get(targetId)!;
        const sourceDepth = source.depth;
        const targetDepth = target.depth;
        const sourceNodeRadius = (sourceDepth === 0) ?
                                    centerNodeRadius :
                                    (sourceDepth === 1 ? firstLevelNodeRadius : secondLevelNodeRadius);
        const targetNodeRadius = (targetDepth === 0) ?
                                    centerNodeRadius :
                                    (targetDepth === 1 ? firstLevelNodeRadius : secondLevelNodeRadius);
        // The old atlas seems to disallow links between nodes at the same
        // level:
        if (source.depth !== target.depth) {
          links.push(createNonCanonicalLink(
            {...source, radiusOfNode: sourceNodeRadius},
            {...target, radiusOfNode: targetNodeRadius},
          ));
        }
      }
    }
  }

  // Sort to avoid unnecessary DOM unmount/remount:
  const sortedLinks = links.sort((a, b) => {
    if (a.source.id === b.source.id) {
      return a.target.id - b.target.id;
    } else {
      return a.source.id - b.source.id;
    }
  });

  const connectionsMap: Record<string, number[]> = {};
  for (const {source: {id: sourceId}, target: {id: targetId}} of sortedLinks) {
    const retrievedSource = connectionsMap[sourceId];
    if (retrievedSource === undefined) {
      connectionsMap[sourceId] = [];
    }
    connectionsMap[sourceId].push(targetId);

    const retrievedTarget = connectionsMap[targetId];
    if (retrievedTarget === undefined) {
      connectionsMap[targetId] = [];
    }
    connectionsMap[targetId].push(sourceId);
  }

  return {
    nodes: nodesWithSize,
    links: sortedLinks,
    connectionsMap,
    centerNodeRadius, firstLevelNodeRadius, secondLevelNodeRadius,
    firstLevelNodeDistanceFromCenter, secondLevelNodeDistanceFromCenter,
    firstLevelSmallestAngularSeparation,
    secondLevelSmallestAngularSeparation,
  };
};

export default getLayout;
